/**
 * Full country list with flags for signup and profile settings.
 * Uses ISO 3166-1 alpha-2 codes; flag emoji derived from code (regional indicator symbols).
 */

import * as countryList from "country-list";

export interface CountryWithFlag {
  code: string;
  name: string;
  flag: string;
}

/** Convert ISO 3166-1 alpha-2 code to flag emoji (regional indicator symbols). */
export function getCountryFlagEmoji(iso2: string): string {
  if (!iso2 || iso2.length !== 2) return "";
  const code = iso2.toUpperCase();
  return code
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 - 65 + c.charCodeAt(0)))
    .join("");
}

let cached: CountryWithFlag[] | null = null;

/**
 * Returns all countries with code, name, and flag, sorted by name.
 * Cached after first call.
 */
export function getCountriesWithFlags(): CountryWithFlag[] {
  if (cached) return cached;
  const data = countryList.getData() as { code: string; name: string }[];
  cached = data
    .map(({ code, name }) => ({
      code,
      name,
      flag: getCountryFlagEmoji(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return cached;
}

/** Legacy export: full list with id (index), name, flag. Kept for any existing consumers. */
export const countryOptions: { id: number; name: string; flag: string }[] =
  getCountriesWithFlags().map((c, i) => ({
    id: i + 1,
    name: c.name,
    flag: c.flag,
  }));
